import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  Input,
  Output,
  EventEmitter,
} from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormGroup,
  UntypedFormBuilder,
  AbstractControl,
  Validators,
  UntypedFormControl,
} from '@angular/forms';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { LegacyPageEvent as PageEvent } from '@angular/material/legacy-paginator';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { tap, takeUntil, debounceTime, distinctUntilChanged } from 'rxjs/operators';

// COMPONENTS

// INTERFACES
import { ILocation } from '../../../location/interfaces/location.interface';
import { ILot } from '../../../lot';

// SERVICES
import {
  LocalStorageKeys,
  LocationApiService,
  PicklistApiService,
  LotApiService,
  OptionInterface,
  SnackBarConfig,
  PaginationConfig,
  DialogConfig,
  SortDirections,
  SystemSettingApiService,
} from '../../../shared';
import { IPagination } from '../../../shared/interfaces/paginator.interface';
import { trackByValue } from '../../../shared/util';
import { BulkShippingLabelComponent } from '../../../shipping';
import {
  PickListInterface,
  PickListItemDraftInterface,
  PickListItemInterface,
  PickListItemStatus,
  PickListItemTableInterface,
  PicklistItemUiInterface,
  PicklistOrderInterface,
  PicklistStatus,
} from '../../interfaces/picklist.interface';
import { PicklistItemDetailsComponent } from '../picklist-item-details/picklist-item-details.component';

@Component({
  selector: 'lum-picklist-items',
  templateUrl: './picklist-items.component.html',
  styleUrls: ['./picklist-items.component.scss'],
})
export class PicklistItemsComponent implements OnInit, OnDestroy {
  @Output()
  closeShippingLabelModalEvent = new EventEmitter<boolean>();

  currentId :number = 0;
  currentPicklist :PickListInterface | null;
  currentPicklist$ :Observable<PickListInterface | null>;
  currentPicklistItems :PickListItemInterface[];
  currentPicklistItems$ :Observable<PickListItemInterface[]>;
  currentRfqId :number;
  currentLocationId :number | null;
  loading$ :Observable<boolean>;
  loadingLocations$ :Observable<boolean>;
  loadingLots$ :Observable<boolean>;
  lotsByRfqId :{ [rfqId :string] :{ [locationId :string] :ILot[] } };
  lotsByRfqId$ :Observable<{
    [rfqId :string] :{ [locationId :string] :ILot[] };
  }>;
  submittingForm$ :Observable<boolean>;
  pagination$ :Observable<IPagination>;
  queryParams :{
    [param :string] :string | string[];
  } = {};

  form :UntypedFormGroup;
  formRows :UntypedFormArray;
  dataSource :PickListItemTableInterface[] = [];
  draftItems :PickListItemDraftInterface[] = [];
  picklistStatus = PicklistStatus;
  searchQuery :string = '';
  paginationConfig = PaginationConfig;
  orderNumberMaxDisplay :number = 4;
  trackByValue = trackByValue;
  origLocationIds :{ [key :number] :number } = {};
  savingItems :boolean = false;
  autoFillQuantityEnabled :boolean;
  locations :any[];

  private onDestroy$ = new Subject();
  loading :boolean = false;
  pickFinishLoading :boolean = false;

  constructor(
    private cd :ChangeDetectorRef,
    private dialog :MatDialog,
    private fb :UntypedFormBuilder,
    private locationApiService :LocationApiService,
    private lotApiService :LotApiService,
    private picklistService :PicklistApiService,
    private route :ActivatedRoute,
    private router :Router,
    private snackBar :MatSnackBar,
    private systemSettingsService :SystemSettingApiService,
  ) {}

  ngOnInit() :void {
    this.picklistService.setCurrentPicklist(null);
    this.initQueryParams();
    this.getVisualPickingAutoFillQuantitySettings();
    this.getFromService();
    this.getRouterParams().subscribe();
    this.getCurrentPicklist().subscribe();
    this.getCurrentPicklistItems().subscribe();
    this.getLotsById().subscribe();
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onPickEvent(item :PicklistItemUiInterface) :void {
    this.currentRfqId = item.rfqId;
    if (item.id && item.locationId) {
      this.currentLocationId = item.locationId;
      this.fetchLocations({
        rfqId: item.rfqId,
        locationId: item.locationId,
      });
      this.fetchLots({
        rfqId: item.rfqId,
        locationId: item.locationId,
      });
    }
    // Update draft items
    const newDraftItem = this.getDraftItem(item);
    if (!this.draftItems.find((item) => item.id === newDraftItem.id)) {
      this.draftItems.push({
        ...newDraftItem,
        locationName: item.selectedLocation?.label,
        lotNumber: item.selectedLot?.label,
        pickedQuantity: newDraftItem.availableQuantity,
      });
    }

    // Update form rows
    const index = this.formRows.value.findIndex(
      (value :PicklistItemUiInterface) => value.id === item.id,
    );
    this.formRows.at(index).patchValue({
      pickedQuantity: this.autoFillQuantityEnabled
        ? newDraftItem.availableQuantity
        : 0,
      status: newDraftItem.status,
    });

    this.updateLocalStorage();
  }

  onDoneEvent(id :number) :void {
    try {
      // We valid the form skus related if required filled is provided
      // or max quantity was splitted proper

      const formRowSkus = this.formRows.controls.filter(
        (control) => control.value.id === id || control.value.parentId === id,
      );
      const isSomeInvalid = formRowSkus.some(
        (control) => control.status === 'INVALID',
      );

      if (isSomeInvalid) {
        throw new Error('Please fill the required fields.');
      }

      const formIndexes = this.formRows.value
        .filter(
          (row :PicklistItemUiInterface) => row.id === id || row.parentId === id,
        )
        .map((row :PicklistItemUiInterface) => {
          return this.formRows.value.findIndex(
            (formRow :PicklistItemUiInterface) =>
              row.id ? row.id === formRow.id : row.tempId === formRow.tempId,
          );
        });

      const status =
        formIndexes.length > 1
          ? [PickListItemStatus.PICKED, PickListItemStatus.SPLIT]
          : [PickListItemStatus.PICKED];

      formIndexes.forEach((index :number) => {
        this.formRows.at(index).patchValue({
          status,
        });
      });

      const draftIndexes = this.draftItems
        .filter((draftItem) => draftItem.id === id || draftItem.parentId === id)
        .map((filteredDraftItem) => {
          return this.draftItems.findIndex((draftItem) =>
            filteredDraftItem.id
              ? filteredDraftItem.id === draftItem.id
              : filteredDraftItem.tempId === draftItem.tempId,
          );
        });

      draftIndexes.forEach((index :number) => {
        this.draftItems[index] = {
          ...this.draftItems[index],
          status,
        };
      });

      this.updateLocalStorage();
    } catch (error :any) {
      this.snackBar.open(error, 'Fail', {
        duration: SnackBarConfig.DEFAULT.duration,
      });
    }
  }

  onResetEvent(id :number) :void {
    // RESET FORM
    const index = this.formRows.controls.findIndex(
      (control :any) => control.value.id === id,
    );

    const originalItem = this.currentPicklistItems.find(
      (item) => item.id === this.formRows.controls[index].value.id,
    );

    if (originalItem) {
      const formattedValue = this.getFormattedItem(originalItem);
      const { locationId, selectedLocation, lotId, selectedLot } = formattedValue;
      this.formRows.at(index).patchValue({
        pickedQuantity: 0,
        locationId,
        selectedLocation,
        lotId,
        selectedLot,
        status: [PickListItemStatus.UNPICKED],
      });
    }

    this.formRows.controls = this.formRows.controls.filter((control) => {
      return control.value.parentId !== id;
    });

    // UPDATE LOCALSTORAGE AND DATASOURCE
    this.draftItems = this.draftItems.filter(
      (item) => item.id !== id && item.parentId !== id,
    );
    this.updateLocalStorage();
  }

  onSplitEvent(id :number) :void {
    try {
      const formIndex = this.formRows.value.findIndex(
        (value :PicklistItemUiInterface) => value.id === id,
      );
      const totalChild = this.formRows.value.filter(
        (value :PicklistItemUiInterface) => value.parentId === id,
      ).length;
      const currentActiveRow :PicklistItemUiInterface =
        this.formRows.value[totalChild + formIndex];

      const item :PicklistItemUiInterface = this.formRows.value.find(
        (value :PicklistItemUiInterface) => value.id === id,
      );
      const status = [PickListItemStatus.IN_PROGRESS, PickListItemStatus.SPLIT];

      this.currentRfqId = item.rfqId;

      if (item.locationId) {
        this.currentLocationId = item.locationId;
      }

      if (!currentActiveRow.pickedQuantity) {
        throw new Error('Invalid quantity.');
      }

      if (!currentActiveRow.locationId) {
        throw new Error('Location is required.');
      }

      if (currentActiveRow.lotTracking && !currentActiveRow.lotId) {
        throw new Error('Lot is required.');
      }

      if (Array.isArray(this.locations) && this.locations.length === 0) {
        throw new Error('No available location');
      }

      if (
        currentActiveRow.availableQuantity -
          Number(currentActiveRow.pickedQuantity) <=
        0
      ) {
        throw new Error('Unable to split due to insufficient items.');
      }

      // UPDATE DRAFT ITEMS
      const draftIndex = this.draftItems.findIndex((d) => d.id === id);
      const newDraftItem = this.getDraftItem(item);
      const tempId = new Date().getTime();
      const updatedDraftItems = this.draftItems.map((draftItem, index) => {
        if (
          index === draftIndex ||
          (draftItem.tempId && draftItem.parentId === item.id)
        ) {
          return {
            ...draftItem,
            status: [...status, PickListItemStatus.DISABLED],
          };
        }

        return draftItem;
      });

      updatedDraftItems.push({
        ...newDraftItem,
        availableQuantity:
          currentActiveRow.availableQuantity -
          Number(currentActiveRow.pickedQuantity),
        id: 0,
        parentId: item.id,
        pickedQuantity:
          currentActiveRow.availableQuantity -
          Number(currentActiveRow.pickedQuantity),
        status,
        locationId: null,
        lotId: null,
        tempId,
      });
      this.draftItems = updatedDraftItems;
      this.updateLocalStorage();

      // CREATE NEW ROW

      this.formRows.controls = this.formRows.controls.map(
        (control :AbstractControl, index :number) => {
          const value :PicklistItemUiInterface = control.value;
          if (index === formIndex || (value.tempId && value.parentId === item.id)) {
            control.setValue({
              ...value,
              status: [...status, PickListItemStatus.DISABLED],
            });
          }

          return control;
        },
      );

      this.formRows.insert(
        formIndex + (totalChild + 1),
        this.createRow({
          ...item,
          availableQuantity:
            currentActiveRow.availableQuantity -
            Number(currentActiveRow.pickedQuantity),
          id: 0,
          parentId: item.id,
          pickedQuantity:
            currentActiveRow.availableQuantity -
            Number(currentActiveRow.pickedQuantity),
          status,
          locationId: null,
          lotId: null,
          selectedLocation: null,
          selectedLot: null,
          tempId,
        }),
      );
    } catch (error :any) {
      this.snackBar.open(error, 'Fail', {
        duration: SnackBarConfig.DEFAULT.duration,
      });
    }
  }

  onRemoveItemEvent(data :{ item :PicklistItemUiInterface }) :void {
    const { item } = data;
    const index = this.formRows.value.findIndex(
      (row :PicklistItemUiInterface) => row.tempId === item.tempId,
    );

    this.formRows.removeAt(index);
    const previousRow = this.formRows.at(index - 1).value;
    const status = previousRow.id
      ? [PickListItemStatus.IN_PROGRESS]
      : [PickListItemStatus.IN_PROGRESS, PickListItemStatus.SPLIT];
    this.formRows.at(index - 1).patchValue({
      status,
    });

    const draftItemIndex = this.draftItems.findIndex(
      (draftItem) => draftItem.tempId === item.tempId,
    );
    this.draftItems[draftItemIndex - 1] = {
      ...this.draftItems[draftItemIndex - 1],
      status,
    };
    this.draftItems = this.draftItems.filter(
      (draftItem) => draftItem.tempId !== item.tempId,
    );
    this.updateLocalStorage();
  }

  onSelectLocationEvent(data :{ location :OptionInterface; id :number }) :void {
    const { location, id } = data;
    this.currentLocationId = location.value;
    const matchedLocation = this.locations.find(
      (loc) => loc.locationId == location.value,
    );

    const warehouseId = matchedLocation?.parent?.id ?? matchedLocation.warehouseId;

    // UPDATE FORM
    const index = this.formRows.value.findIndex(
      (val :any) => val.id === id || val.tempId === id,
    );
    this.formRows.at(index).patchValue({
      locationId: location.value,
      lotId: null,
      selectedLot: null,
      warehouseId: warehouseId,
    });

    const draftItemIndex = this.draftItems.findIndex(
      (item) => item.id === id || item.tempId === id,
    );

    this.draftItems[draftItemIndex] = {
      ...this.draftItems[draftItemIndex],
      locationId: Number(location.value),
      locationName: location.label,
      warehouseId: warehouseId,
    };

    this.updateLocalStorage();
  }

  onSelectLotEvent(data :{ lot :OptionInterface; id :number }) :void {
    const { lot, id } = data;
    // UPDATE FORM
    const index = this.formRows.value.findIndex(
      (val :any) => val.id === id || val.tempId === id,
    );
    this.formRows.at(index).patchValue({
      lotId: lot.value,
    });

    // UPDATE DRAFT AND LOCALSTORAGE ITEMS
    const draftItemIndex = this.draftItems.findIndex(
      (item) => item.id === id || item.tempId === id,
    );

    this.draftItems[draftItemIndex] = {
      ...this.draftItems[draftItemIndex],
      lotId: Number(lot.value),
      lotNumber: lot.label,
    };

    this.updateLocalStorage();
  }

  fetchLocations(data :{ rfqId :number; locationId :number }) :void {
    const { rfqId, locationId } = data;
    this.currentRfqId = rfqId;
    this.currentLocationId = locationId;
    const currentPicklistItem = this.currentPicklistItems.find(
      (item) => item.rfqId === rfqId,
    );

    if (currentPicklistItem) {
      const locationStocks = currentPicklistItem?.rfq?.locationStocks;

      if (locationStocks.length > 0) {
        const consolidatedStocks :any[] = [];

        locationStocks.forEach((stock :any) => {
          const stockCopy = { ...stock, stock_locations: [] };

          stock.stock_locations.forEach((location :any) => {
            const existingLocation = stockCopy.stock_locations.find(
              (loc) => loc.location_name === location.location_name,
            );

            if (existingLocation) {
              existingLocation.pivot.onhand_quantity =
                parseFloat(existingLocation.pivot.onhand_quantity) +
                parseFloat(location.pivot.onhand_quantity);

              existingLocation.pivot.reserved_quantity =
                parseFloat(existingLocation.pivot.reserved_quantity) +
                parseFloat(location.pivot.reserved_quantity);
            } else {
              stockCopy.stock_locations.push({
                ...location,
                pivot: { ...location.pivot },
              });
            }
          });

          consolidatedStocks.push(stockCopy);
        });

        const formattedLocation = consolidatedStocks.map((item) => {
          const warehouseName = item.warehouse.location_name;
          const warehouseId = item.warehouse.id;

          return item.stock_locations.map((stockLocation) => ({
            warehouseId: warehouseId,
            warehouseName: warehouseName,
            locationId: stockLocation.id,
            locationName: stockLocation.location_name,
            onhandQuantity: stockLocation.pivot.onhand_quantity,
          }));
        });

        this.locations = formattedLocation.flat();
      }
    }
  }

  fetchLots(data :{ rfqId :number; locationId :number }) :void {
    const { rfqId, locationId } = data;
    this.currentRfqId = rfqId;
    this.currentLocationId = locationId;
    // CHECK IF RFQ ID LOCATIONS ALREADY LOADED
    if (
      this.currentLocationId &&
      (!this.lotsByRfqId ||
        !Object.keys(this.lotsByRfqId).length ||
        !this.lotsByRfqId[rfqId] ||
        !this.lotsByRfqId[rfqId][this.currentLocationId])
    ) {
      // FETCH FROM DB
      this.lotApiService.getById({
        id: rfqId,
        params: {
          locationId: locationId.toString(),
        },
      });
    }
  }

  onSearchEvent(searchQuery :string) :void {
    this.searchQuery = searchQuery;
    this.loadPicklistItems();
  }

  onPaginationEvent(pagination :PageEvent) :void {
    this.queryParams = {
      ...this.queryParams,
      limit: pagination.pageSize.toString(),
      page: (pagination.pageIndex + 1).toString(),
    };
    this.loadPicklistItems();
  }

  onShip() :void {
    const items = this.formRows.value.map((row :PicklistItemUiInterface) => {
      return {
        id: row.id,
        pickedQuantity: Number(row.pickedQuantity),
        locationId: row.locationId,
        lotId: row.lotId,
        parentId: row.parentId || null,
        availableQuantity: row.availableQuantity,
        warehouseId: row.warehouseId,
      };
    });

    if (this.currentPicklist) {
      this.pickFinishLoading = true;
      this.picklistService
        .ship({
          id: this.currentPicklist.id,
          items,
        })
        .pipe(takeUntil(this.onDestroy$))
        .subscribe({
          next: (response :{ success :string }) => {
            localStorage.removeItem(LocalStorageKeys.PICKLISTS);
            this.snackBar.open(response.success, 'Success', {
              duration: SnackBarConfig.DEFAULT.duration,
            });
            this.router.navigate(['shipping/packing', this.currentId]);
            this.pickFinishLoading = false;
          },
          error: (error) => {
            const message = typeof error === 'string' ? error : error?.error;

            this.snackBar.open(message ?? 'Could not finish picklist.', 'Fail', {
              duration: SnackBarConfig.DEFAULT.duration,
            });
            this.pickFinishLoading = false;

            // if no message was found in the places we expect on the response,
            // we print the whole error to the console so developers can more
            // easily figure out what's going on. [calvin]
            if (!message) {
              console.error(error);
            }
          },
        });
    }
  }

  onSave() :void {
    const items = this.formRows.value
      .filter(
        (row :PicklistItemUiInterface) =>
          row.status[0] !== PickListItemStatus.UNPICKED && !row.isUpdated,
      )
      .map((row :PicklistItemUiInterface) => {
        return {
          id: row.id,
          pickedQuantity: Number(row.pickedQuantity),
          locationId: row.locationId,
          lotId: row.lotId,
          parentId: row.parentId || null,
          availableQuantity: row.availableQuantity,
          warehouseId: row.warehouseId,
        };
      });

    if (this.currentPicklist) {
      this.savingItems = true;
      this.picklistService
        .savePicklist({
          id: this.currentPicklist.id,
          items,
        })
        .pipe(takeUntil(this.onDestroy$))
        .subscribe({
          next: (response :{ success :string }) => {
            this.snackBar.open(response.success, 'Success', {
              duration: SnackBarConfig.DEFAULT.duration,
            });
            localStorage.removeItem(LocalStorageKeys.PICKLISTS);
            this.draftItems = [];
            this.loadPicklistItems();
            this.savingItems = false;
          },
          error: (response :any) => {
            this.snackBar.open(response.error, 'Fail', {
              duration: SnackBarConfig.DEFAULT.duration,
            });
            this.savingItems = false;
          },
        });
    }
  }

  onShowSkuDetailEvent(item :PicklistItemUiInterface) :void {
    this.dialog.open(PicklistItemDetailsComponent, {
      data: {
        item,
      },
      width: DialogConfig.sizes.small,
    });
  }

  onSortEvent(data :{ sortField :string }) :void {
    this.queryParams = {
      ...this.queryParams,
      sortField: data.sortField,
      sortDirection:
        !this.queryParams['sortDirection'] ||
        (this.queryParams['sortDirection'] &&
          this.queryParams['sortDirection'] === SortDirections.DESC)
          ? SortDirections.ASC
          : SortDirections.DESC,
    };
    this.loadPicklistItems();
  }

  onMultiplePickEvent() :void {
    this.checkedItemsForPick?.forEach((item :AbstractControl) => {
      this.onPickEvent(item?.value);
    });
  }

  onMultipleDoneEvent() :void {
    this.checkedItemsForDone?.forEach((item :AbstractControl) => {
      this.onDoneEvent(item?.value?.id);
    });
  }

  onMultipleResetEvent() :void {
    this.checkedItemsForReset?.forEach((item :AbstractControl) => {
      this.onResetEvent(item?.value?.id);
    });
  }

  printShippingLabels() :void {
    if (this.currentPicklist?.orders && this.currentPicklist?.orders?.length > 0) {
      this.loading = true;
      const salesOrderIds = this.currentPicklist?.orders?.map((order) => order.id);

      this.picklistService
        .getPickingItemsBySalesOrderIds({
          salesOrderIds: salesOrderIds.join(','),
          includeWithPicklist: 'true',
        })
        .pipe(takeUntil(this.onDestroy$))
        .subscribe({
          next: (response) => {
            this.loading = false;
            this.cd.detectChanges();

            this.dialog
              .open(BulkShippingLabelComponent, {
                data: {
                  orders: response.orders ?? [],
                  warehouseAddress: response.warehouseAddress ?? null,
                },
                width: DialogConfig.sizes['xxx-large'],
              })
              .afterClosed()
              .pipe(takeUntil(this.onDestroy$))
              .subscribe((response) => {
                if (response) {
                  this.closeShippingLabelModalEvent.emit(true);

                  if (this.currentPicklist?.id) {
                    this.router.navigate([
                      'shipping/picklists',
                      this.currentPicklist?.id,
                    ]);
                  } else {
                    this.router.navigate(['shipping/picklists']);
                  }
                }
              });
          },
          error: (response) => {
            this.loading = false;
            this.cd.detectChanges();

            this.snackBar.open(response.error, 'Fail', {
              duration: SnackBarConfig.DEFAULT.duration,
            });
          },
        });
    }
  }

  get lots() :ILot[] {
    const defaultSelectedLotIds = this.formRows.value
      .map((row :PicklistItemUiInterface) => row.lotId)
      .filter((id :number) => id);
    if (
      this.lotsByRfqId &&
      this.currentRfqId &&
      this.currentLocationId &&
      this.lotsByRfqId[this.currentRfqId] &&
      this.lotsByRfqId[this.currentRfqId][this.currentLocationId]
    ) {
      const lots :any = [];

      Object.entries(this.lotsByRfqId[this.currentRfqId]).forEach(
        ([outerKey, innerObjects]) => {
          Object.entries(innerObjects).forEach(([innerKey, lotObject]) => {
            let exists = false;
            for (const existingLot of lots || []) {
              if (existingLot.id === lotObject.id) {
                exists = true;
                break;
              }
            }

            if (!exists) {
              lots.push(lotObject);
            }
          });
        },
      );

      return lots;
    } else {
      return defaultSelectedLotIds;
    }
  }

  get selectedLotIds() :{ id :number; locationId :number; rfqId :number }[] {
    return this.formRows.value
      .filter(
        (row :PicklistItemUiInterface) =>
          row.rfqId === this.currentRfqId && row.lotId,
      )
      .map((row :PicklistItemUiInterface) => ({
        id: row.lotId,
        locationId: row.locationId,
        rfqId: row.rfqId,
      }));
  }

  get itemPickedCount() :number {
    if (this.formRows) {
      const pickedItems :number[] = [];
      const itemIds :number[] = [];

      this.formRows.value.forEach((item :PicklistItemUiInterface) => {
        pickedItems.push(Number(item.pickedQuantity));
        itemIds.push((item.id || item.tempId) as number);
      });

      this.draftItems.forEach((draftItem) => {
        if (!itemIds.includes(draftItem.id || (draftItem.tempId as number))) {
          pickedItems.push(Number(draftItem.pickedQuantity));
        }
      });

      return pickedItems.reduce(
        (accumulator :number, currentValue :number) => accumulator + currentValue,
        0,
      );
    }

    return 0;
  }

  get hasUnpicked() :boolean {
    if (this.formRows) {
      return this.formRows.value.some(
        (row :PicklistItemUiInterface) =>
          row.status.includes(PickListItemStatus.UNPICKED) ||
          row.status.includes(PickListItemStatus.IN_PROGRESS),
      );
    }

    return false;
  }

  get hasLotTracking() :boolean {
    return this.formRows?.value?.some(
      (item :PicklistItemUiInterface) => item.lotTracking === true,
    );
  }

  get rfqByLocationIdsUnique() :{ rfqId :number; locationId :number }[] {
    const rfqByLocationIdsFormatted = this.draftItems.map((item) => ({
      rfqId: item.rfqId,
      locationId: item.locationId,
    }));

    return Array.from(
      new Set(rfqByLocationIdsFormatted.map((item) => item.locationId)),
    ).map((locationId) => {
      return rfqByLocationIdsFormatted.find(
        (item) => item.locationId === locationId,
      );
    }) as { rfqId :number; locationId :number }[];
  }

  get orders() :PicklistOrderInterface[] {
    if (this.currentPicklist?.orders?.length) {
      return this.currentPicklist.orders;
    }

    return [];
  }
  get orderNumbers() :string[] {
    if (this.currentPicklist?.orderNumbers?.length) {
      return this.currentPicklist.orderNumbers.map((order) => order.orderNumber);
    }

    return [];
  }

  get checkedItems() :number {
    return this.formRows?.controls?.filter(
      (item :AbstractControl) => item.value.checked,
    ).length;
  }

  get checkedItemsForPick() :AbstractControl[] {
    if (!this.formRows) {
      return [];
    }

    return this.formRows.controls.filter(
      (item :AbstractControl) =>
        item.value.checked &&
        item?.value?.status?.includes(PickListItemStatus.UNPICKED),
    );
  }

  get checkedItemsForDone() :AbstractControl[] {
    if (!this.formRows) {
      return [];
    }

    return this.formRows.controls.filter(
      (item :AbstractControl) =>
        item.value.checked &&
        item?.value?.status?.includes(PickListItemStatus.IN_PROGRESS) &&
        !item?.value?.parentId,
    );
  }

  get checkedItemsForReset() :AbstractControl[] {
    if (!this.formRows) {
      return [];
    }

    return this.formRows.controls.filter(
      (item :AbstractControl) =>
        item.value.checked &&
        item?.value?.status?.includes(PickListItemStatus.PICKED) &&
        !item?.value?.parentId,
    );
  }

  get hasInProgressOrPickedItems() :boolean {
    if (!this.formRows) {
      return false;
    }

    return this.formRows.controls.some(
      (item :AbstractControl) =>
        (item?.value?.status?.includes(PickListItemStatus.IN_PROGRESS) ||
          item?.value?.status?.includes(PickListItemStatus.PICKED)) &&
        !item?.value?.isUpdated,
    );
  }

  get pickedAllItems() :boolean {
    if (!this.formRows) {
      return false;
    }

    return this.formRows.controls.every(
      (item :AbstractControl) =>
        item?.value?.status?.includes(PickListItemStatus.PICKED) &&
        item?.value?.isUpdated,
    );
  }

  get canSaveItems() :boolean {
    return (
      !this.pickedAllItems &&
      (this.currentPicklist?.status === PicklistStatus.CREATED ||
        this.currentPicklist?.status === PicklistStatus.PICKING)
    );
  }

  get canUpdateCheckedItemsForPick() :boolean {
    if (!this.formRows) {
      return false;
    }

    return this.checkedItemsForPick.every(
      (item :AbstractControl) => !item?.value?.isUpdated,
    );
  }

  get canUpdateCheckedItemsForDone() :boolean {
    if (!this.formRows) {
      return false;
    }

    return this.checkedItemsForDone.every(
      (item :AbstractControl) => !item?.value?.isUpdated,
    );
  }

  get canUpdateCheckedItemsForReset() :boolean {
    if (!this.formRows) {
      return false;
    }

    return this.checkedItemsForReset.every(
      (item :AbstractControl) => !item?.value?.isUpdated,
    );
  }

  private initDraftItems() :void {
    const draftItems = JSON.parse(
      localStorage.getItem(LocalStorageKeys.PICKLISTS) || '{}',
    );

    if (draftItems[this.currentId]) {
      this.draftItems = draftItems[this.currentId];
    }
  }

  private loadDraftedLocations() :void {
    this.rfqByLocationIdsUnique.forEach((data) => {
      if (data && data.locationId && data.rfqId) {
        this.fetchLocations({
          locationId: data.locationId,
          rfqId: data.rfqId,
        });
      }
    });
  }

  private loadDraftedLots() :void {
    this.rfqByLocationIdsUnique.forEach((data) => {
      if (data && data.locationId && data.rfqId) {
        this.fetchLots({
          locationId: data.locationId,
          rfqId: data.rfqId,
        });
      }
    });
  }

  private initQueryParams() :void {
    this.queryParams = {
      relations:
        'rfq.locationStock.rfqStockLocations.location.parent,order,orderItem,orderItem.baseUom,location,lot,location.parent,location.rfqstockLocations,rfq.locationStocks.stockLocations,rfq.locationStocks.warehouse',
    };
  }

  private getRouterParams() :Observable<Params> {
    return this.route.params.pipe(
      tap((params :Params) => {
        const { id } = params;
        if (id) {
          this.currentId = id;
          this.initDraftItems(); // FROM LOCALSTORAGE
          this.loadPickList({ id });
          this.loadPicklistItems();
        }
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private loadPickList(data :{ id :number }) :void {
    const { id } = data;

    this.picklistService.getById({
      id,
      params: {
        relations: 'itemsWithTotalUnits,createdByUser,orderNumbers,orders',
      },
    });
  }

  private loadPicklistItems() :void {
    this.picklistService.getItems({
      picklistId: this.currentId.toString(),
      searchQuery: this.searchQuery,
      ...this.queryParams,
    });
  }

  private getFromService() :void {
    this.currentPicklist$ = this.picklistService.currentPicklist$;
    this.currentPicklistItems$ = this.picklistService.currentPicklistItems$;
    this.loading$ = this.picklistService.loading$;
    this.loadingLocations$ = this.locationApiService.loading$;
    this.loadingLots$ = this.lotApiService.loading$;
    this.lotsByRfqId$ = this.lotApiService.lotsByRfqId$;
    this.pagination$ = this.picklistService.pagination$;
    this.submittingForm$ = this.picklistService.submittingForm$;
  }

  private getCurrentPicklist() :Observable<PickListInterface | null> {
    return this.currentPicklist$.pipe(
      tap((picklist) => {
        if (picklist && picklist.id) {
          this.currentPicklist = picklist;
          if (
            picklist.status === PicklistStatus.CREATED ||
            picklist.status === PicklistStatus.PICKING
          ) {
            this.picklistService
              .updatePicklistStatus({
                id: this.currentPicklist.id,
                picklistStatus: PicklistStatus.PICKING,
              })
              .pipe(takeUntil(this.onDestroy$))
              .subscribe();
          }

          if (picklist.status === PicklistStatus.SHIPPED) {
            this.router.navigate(['shipping/picklists', picklist.id]);
          } else if (picklist.status === PicklistStatus.PICKED) {
            this.router.navigate(['shipping/visual-picking', picklist.id]);
          }
        }
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private getCurrentPicklistItems() :Observable<PickListItemInterface[]> {
    return this.currentPicklistItems$.pipe(
      tap((items :PickListItemInterface[]) => {
        if (items) {
          this.currentPicklistItems = items;
          if (items?.length) {
            this.removeProgressOnQtyDiff(items);
            this.initForm();
            this.getFormArrayChanges();

            if (this.draftItems?.length) {
              this.loadDraftedLocations();
              this.loadDraftedLots();
            }
          }
        }
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private getLotsById() :Observable<{
    [rfqId :string] :{ [locationId :string] :any[] };
  }> {
    return this.lotsByRfqId$.pipe(
      tap((lots) => {
        this.lotsByRfqId = lots;
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private initForm() :void {
    this.form = this.fb.group({
      rows: this.fb.array([]),
    });
    this.formRows = this.form.get('rows') as UntypedFormArray;

    // We need to include the draft items when initializing the form array

    const formattedItems :PicklistItemUiInterface[] = this.currentPicklistItems.map(
      (item) => {
        return this.getFormattedItem(item);
      },
    );

    formattedItems.forEach((item) => {
      this.formRows.push(this.createRow(item));

      if (this.draftItems.length) {
        const splittedItemsFromParent = this.draftItems.filter(
          (draftItem) =>
            !draftItem.id && draftItem.tempId && draftItem.parentId === item.id,
        );

        splittedItemsFromParent.forEach((splittedItem) => {
          this.formRows.push(
            this.createRow({
              ...item,
              ...splittedItem,
              selectedLocation: {
                value: splittedItem.locationId,
                label: splittedItem.locationName || '',
                subLabel: '',
              },
              selectedLot: {
                value: splittedItem.lotId,
                label: splittedItem.lotNumber || '',
                subLabel: '',
              },
            }),
          );
        });
      }
    });
  }

  isFieldRequiredValidator(isRequired :boolean) :Validators {
    return (control :UntypedFormControl) => {
      return isRequired ? Validators.required(control) : null;
    };
  }

  private getItemStatus(status :any) :PickListItemStatus[] {
    switch (status) {
      case PicklistStatus.SHIPPED:
        return [PickListItemStatus.SHIPPED];
      case PicklistStatus.PICKED:
        return [PickListItemStatus.PICKED];

      default:
        return [PickListItemStatus.UNPICKED];
    }
  }

  private getMatchedItemFromDrafts(
    item :PickListItemInterface,
  ) :PickListItemDraftInterface | undefined {
    if (this.draftItems?.length) {
      return this.draftItems.find(
        (draftItem) => draftItem.id === item.id && !draftItem.tempId,
      );
    }

    return undefined;
  }

  private getFormattedItem(item :PickListItemInterface) :PicklistItemUiInterface {
    const status = this.getItemStatus(item.status);
    // Check if item has draft status
    const matchedItemFromDraft = this.getMatchedItemFromDrafts(item);
    const hasRfqStockLocations =
      item?.location?.rfqStockLocations?.filter(
        (rfqStockLocation) => rfqStockLocation,
      ).length !== 0;

    const notInRfqStockLocations =
      item?.rfq?.locationStock?.rfq_stock_locations.filter(
        (row :any) =>
          row.location_id === item?.locationId && row.onhand_quantity > 0,
      ).length === 0;

    let selectedLocationValue = null;
    let selectedLocationLabel = '';
    if (matchedItemFromDraft && hasRfqStockLocations) {
      selectedLocationValue = Number(matchedItemFromDraft?.locationId);
      selectedLocationLabel = matchedItemFromDraft?.locationName || '';
    } else {
      const useFirstRfqStockLocation =
        !hasRfqStockLocations && notInRfqStockLocations;
      const firstRfqStockLocation =
        item?.rfq?.locationStock?.rfq_stock_locations?.[0];

      selectedLocationValue = useFirstRfqStockLocation
        ? firstRfqStockLocation?.location_id
        : item?.locationId;

      selectedLocationLabel = useFirstRfqStockLocation
        ? `${firstRfqStockLocation?.location?.location_name} - (${firstRfqStockLocation?.location?.parent?.location_name})`
        : `${item?.location?.locationName} - (${item?.location?.parent?.locationName})`;
    }

    return {
      id: item?.id,
      availableQuantity:
        (matchedItemFromDraft && Number(matchedItemFromDraft.availableQuantity)) ||
        item?.availableQuantity,
      baseUom: item?.orderItem?.baseUom?.name,
      warehouseId:
        (matchedItemFromDraft && Number(matchedItemFromDraft.warehouseId)) ||
        item?.location?.parentId,
      locationId: selectedLocationValue,
      lotId:
        (matchedItemFromDraft && Number(matchedItemFromDraft.lotId)) || item?.lotId,
      lotTracking: item?.rfq?.lotTracking,
      orderDate: item?.order?.orderDate,
      parentId: (matchedItemFromDraft && matchedItemFromDraft.parentId) || null,
      pickedQuantity:
        (matchedItemFromDraft && Number(matchedItemFromDraft.pickedQuantity)) ||
        Number(item?.pickedQuantity),
      productDescription: item?.rfq?.productDescription,
      productName: item?.rfq?.productName,
      internalSku: item?.rfq?.internalSku,
      rfqId: item?.rfq?.id,
      rfqPhoto: item?.rfq?.photo,
      selectedLocation: {
        value: selectedLocationValue,
        label: selectedLocationLabel,
        subLabel: '',
      },
      selectedLot: {
        value: matchedItemFromDraft
          ? Number(matchedItemFromDraft?.lotId)
          : item?.lotId,
        label:
          (matchedItemFromDraft
            ? matchedItemFromDraft?.lotNumber
            : item?.lot?.lotNumber) || '',
        subLabel: '',
      },
      status: (matchedItemFromDraft && matchedItemFromDraft?.status) || status,
      tempId: (matchedItemFromDraft && matchedItemFromDraft?.tempId) || null,
      updatedAt: item?.updatedAt,
      checked: item?.checked || false,
      isUpdated: item.savedAt !== null,
    };
  }

  private createRow(item :PicklistItemUiInterface) :UntypedFormGroup {
    return this.fb.group({
      id: [item.id],
      availableQuantity: [item.availableQuantity],
      baseUom: [item.baseUom],
      locationId: [item.locationId, [Validators.required]],
      lotId: [item.lotId, [this.isFieldRequiredValidator(item.lotTracking)]],
      lotTracking: [item.lotTracking],
      orderDate: [item.orderDate],
      parentId: [item.parentId],
      pickedQuantity: [
        item.pickedQuantity,
        [Validators.required, Validators.min(0)],
      ],
      productDescription: [item.productDescription],
      productName: [item.productName],
      internalSku: [item.internalSku],
      rfqId: [item.rfqId],
      rfqPhoto: [item.rfqPhoto],
      selectedLocation: [item.selectedLocation],
      selectedLot: [item.selectedLot],
      status: [item.status],
      tempId: [item.tempId],
      warehouseId: [item.warehouseId],
      checked: [item?.checked],
      isUpdated: [item.isUpdated],
    });
  }

  private getFormArrayChanges() :void {
    this.formRows.valueChanges
      .pipe(
        debounceTime(1000),
        distinctUntilChanged(),
        tap((data :PicklistItemUiInterface[]) => {
          try {
            for (const row of data) {
              if (Number(row.pickedQuantity) > row.availableQuantity) {
                const formIndex = this.formRows.value.findIndex(
                  (value :PicklistItemUiInterface) =>
                    row.id ? value.id === row.id : value.tempId === row.tempId,
                );

                this.formRows.at(formIndex).patchValue({ pickedQuantity: 0 });

                this.updateDraftItems([{ ...row, pickedQuantity: 0 }]);
                this.updateLocalStorage();

                this.cd.detectChanges();
                throw new Error('Invalid quantity.');
              }
            }

            this.updateDraftItems(data);
            this.updateLocalStorage();
          } catch (error :any) {
            this.snackBar.open(error, 'Fail', {
              duration: SnackBarConfig.DEFAULT.duration,
            });
          }
        }),
        takeUntil(this.onDestroy$),
      )
      .subscribe();
  }

  private updateDraftItems(formValues :PicklistItemUiInterface[]) :void {
    formValues.forEach((item :any) => {
      let index = 0;
      let selectedLocation :ILocation | null = null;
      let selectedLot :ILot | null = null;

      if (this.locations?.length) {
        selectedLocation =
          this.locations.find((location) => location.id === item.locationId) ||
          null;
      }

      if (this.lots?.length) {
        selectedLot = this.lots.find((lot) => lot.id === item.lotId) || null;
      }

      if (item.tempId) {
        index = this.draftItems.findIndex(
          (draftItem) => draftItem.tempId === item.tempId,
        );
      } else {
        index = this.draftItems.findIndex(
          (draftItem) => draftItem.id === item.id && !item.parentId,
        );
      }

      const matchedDraftItem = this.draftItems[index];

      if (index > -1) {
        const updatedDraftItems :PickListItemDraftInterface[] = JSON.parse(
          JSON.stringify(this.draftItems),
        );

        updatedDraftItems[index] = {
          ...matchedDraftItem,
          locationId: item.locationId,
          lotId: item.lotId,
          locationName:
            (selectedLocation &&
              `${selectedLocation.locationName} - ${selectedLocation?.parent?.locationName}`) ||
            matchedDraftItem.locationName,
          lotNumber:
            (selectedLot && selectedLot.lotNumber) || matchedDraftItem.lotNumber,
          pickedQuantity: Number(item.pickedQuantity),
        };

        this.draftItems = updatedDraftItems;
      }
    });
  }

  private updateLocalStorage() :void {
    localStorage.setItem(
      LocalStorageKeys.PICKLISTS,
      JSON.stringify({
        [this.currentId]: this.draftItems,
      }),
    );
  }

  private getDraftItem(item :PicklistItemUiInterface) :PickListItemDraftInterface {
    const {
      id,
      pickedQuantity,
      locationId,
      lotId,
      parentId,
      availableQuantity,
      warehouseId,
      rfqId,
    } = item;

    return {
      id,
      tempId: null,
      status: [PickListItemStatus.IN_PROGRESS],
      pickedQuantity: pickedQuantity || 0,
      locationId,
      lotId,
      parentId,
      availableQuantity,
      warehouseId,
      rfqId,
    };
  }

  private hasAvailableLots(locationId :number) :boolean {
    const lots =
      this.lotsByRfqId[this.currentRfqId] &&
      this.lotsByRfqId[this.currentRfqId][locationId];

    if (lots === undefined) {
      return true;
    }

    const availableLots = lots?.filter((lot) => lot.rfqId === this.currentRfqId);

    return availableLots?.length ? true : false;
  }

  private removeProgressOnQtyDiff(items :PickListItemInterface[]) :void {
    items.forEach((item) => {
      const draftItemIdx = this.draftItems?.findIndex(
        (draftItem) => draftItem.id === item.id,
      );

      if (
        draftItemIdx >= 0 &&
        item.availableQuantity !== this.draftItems[draftItemIdx].availableQuantity
      ) {
        this.draftItems.splice(draftItemIdx, 1);
      }
    });

    this.updateLocalStorage();
  }

  private getVisualPickingAutoFillQuantitySettings() :void {
    this.systemSettingsService
      .getSettingsByKey('visual_picking_auto_fill_quantity')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((setValue :boolean) => {
        this.autoFillQuantityEnabled = setValue;
      });
  }
}
